import { useMemo } from 'react';
import { useClientData } from '../contexts/ClientDataContext';
import { BUSINESS_CONFIG } from '../utils/stepRequirements';

/**
 * useStepAccessControl Hook
 *
 * Provides step-based access control for the tax preparation workflow.
 * Handles both personal and business accounts with configurable requirements.
 *
 * @returns {Object} Access control methods and state
 */
const useStepAccessControl = () => {
  const { clientData } = useClientData();

  // Determine account type
  const accountType = useMemo(() => {
    return clientData?.client?.profile?.account_type || 'personal';
  }, [clientData?.client?.profile?.account_type]);

  const isBusinessAccount = accountType === 'business';

  // Check if step blocking is enabled for current account type
  const stepBlockingEnabled = useMemo(() => {
    return isBusinessAccount ? BUSINESS_CONFIG.APPLY_STEP_BLOCKING : true;
  }, [isBusinessAccount]);

  // Check if questionnaire is required for current account type
  const questionnaireRequired = useMemo(() => {
    return isBusinessAccount ? BUSINESS_CONFIG.REQUIRE_QUESTIONNAIRE : true;
  }, [isBusinessAccount]);

  // Backend-controlled access flags for major routes
  const canAccessQuestionnaire = useMemo(() => {
    if (!clientData?.clientProgress?.accessControl) return false;
    return clientData.clientProgress.accessControl.canAccessQuestionnaire ?? false;
  }, [clientData]);

  const canAccessDocumentUpload = useMemo(() => {
    if (!clientData?.clientProgress?.accessControl) return false;
    return clientData.clientProgress.accessControl.canAccessDocumentUpload ?? false;
  }, [clientData]);

  /**
   * Check if user can access a specific step
   * @param {number} step - Step number (0-5)
   * @returns {boolean} Whether the step is accessible
   */
  const canAccessStep = (step) => {
    // If no client data yet, deny access (loading state)
    if (!clientData) return false;

    const progress = clientData.clientProgress;
    if (!progress || !progress.steps) return false;

    // Business accounts bypass blocking (if configured)
    if (isBusinessAccount && !stepBlockingEnabled) {
      return true;
    }

    // First step (index 0) is always accessible
    if (step === 0) return true;

    // Current step from backend is accessible
    if (progress.currentStep === step) return true;

    // Already completed steps are accessible (for review)
    if (progress.steps[step] === true) return true;

    // Can access next step if previous step is complete
    if (step > 0 && progress.steps[step - 1] === true) return true;

    return false;
  };

  /**
   * Get the blocking reason for a specific step
   * @param {number} step - Step number (0-5)
   * @returns {string} User-facing error message
   */
  const getBlockingReason = (step) => {
    if (!clientData) return 'Loading...';

    const progress = clientData.clientProgress;
    if (!progress) return 'Please wait...';

    const previousStepName = progress.stepLabels?.[step - 1]?.label || `Step ${step}`;

    console.log('CurrentStep: ', step);
    console.log('ProgressSteps: ', progress.steps);
    if (step > 0 && !progress.steps[step - 1]) {
      return `Please complete ${previousStepName} first`;
    }

    return 'Please complete previous steps';
  };

  /**
   * Get all missing requirements for a specific step
   * @param {number} step - Step number (0-5)
   * @returns {Array} Array of failed requirement checks
   */
  const getMissingRequirements = (step) => {
    if (!clientData) return [];

    const progress = clientData.clientProgress;
    if (!progress) return [];

    const missing = [];

    for (let i = 0; i < step; i++) {
      if (!progress.steps[i]) {
        missing.push({
          step: i,
          name: progress.stepLabels?.[i]?.label || `Step ${i + 1}`,
          message: `Complete ${progress.stepLabels?.[i]?.label || `Step ${i + 1}`}`
        });
      }
    }

    return missing;
  };

  /**
   * Get step name for display
   * @param {number} step - Step number (0-5)
   * @returns {string} Step name
   */
  const getStepName = (step) => {
    const progress = clientData?.clientProgress;
    return progress?.stepLabels?.[step]?.label || `Step ${step + 1}`;
  };

  /**
   * Check if all requirements are met for the current account
   * @returns {Object} Completion status for each step
   */
  const getCompletionStatus = () => {
    if (!clientData?.clientProgress?.steps) return {};

    const progress = clientData.clientProgress;
    const status = {};

    progress.steps.forEach((completed, index) => {
      status[`step${index}`] = canAccessStep(index);
    });

    status.allComplete = progress.steps.every(step => step === true);

    return status;
  };

  return {
    // Access control methods
    canAccessStep,
    canAccessQuestionnaire,
    canAccessDocumentUpload,
    getBlockingReason,
    getMissingRequirements,
    getStepName,
    getCompletionStatus,

    // Account type information
    accountType,
    isBusinessAccount,
    stepBlockingEnabled,
    questionnaireRequired,

    // Client data state
    hasClientData: !!clientData,
    currentStep: clientData?.clientProgress?.currentStep,
    currentStepLabel: clientData?.currentStepLabel
  };
};

export default useStepAccessControl;
